import { approvedAdChannelCount } from '../channels';
import { type Feature, isFeatureInPlan } from '../features';
import { PLANS } from '../plans';

export type PlanAdditionId = 'channels' | 'attribution' | 'simulations' | 'rebalancing' | 'validation';

export type PlanAddition = {
  /**
   * Stable key, and the only thing the row carries about its icon.
   *
   * The lucide component lives in `UpsellDialog`'s `ADDITION_ICONS`, keyed by this id, so this
   * module stays a `.ts` with no JSX and no view dependency — which is what lets it run in the
   * node vitest project. `Record<PlanAdditionId, …>` over there makes the mapping exhaustive,
   * so adding a row here fails `npm run ts` in the renderer rather than rendering an icon hole.
   */
  id: PlanAdditionId;
  title: string;
  description: string;
  /**
   * The one `Feature` this row stands for. Typed `Feature`, never `string`: a renamed key fails
   * `npm run ts` here rather than at runtime, where `isFeatureInPlan` fails CLOSED on an
   * unrecognised key — the row would never be filtered out and the dialog would sell a team
   * something it already owns.
   */
  feature: Feature;
};

/**
 * What the premium plan adds on top of Nexoya Starter, in reading order.
 *
 * ADDITIONS ONLY. There is no included-first ledger any more: "what you already have" is
 * carried by the dialog's opening sentence, which is where a buyer actually reads it rather
 * than counting green ticks.
 *
 * Every string here is vendor-neutral on purpose. The same rows render on a white-label
 * tenant, where a Nexoya-branded sales pitch is the one thing that must not appear (D6), and
 * one neutral list is cheaper to keep honest than two that drift.
 */
const ADDITIONS: readonly PlanAddition[] = [
  {
    id: 'channels',
    feature: 'channel.unrestricted',
    title: 'Every supported channel',
    // Derived, never authored: a hardcoded "seven" drifts the day someone edits
    // `APPROVED_CHANNEL_KEYS`, and this sentence is the only place in the product that
    // quantifies the allow-list to a buyer. `channels.test.ts` and `planAdditions.test.ts`
    // both pin the number, so an edit there fails CI instead of shipping a wrong claim.
    description: `Beyond the ${approvedAdChannelCount()} channels your current plan connects.`,
  },
  {
    id: 'attribution',
    feature: 'attribution.view',
    title: 'Attribution',
    description: 'See what actually drove each result across the funnel.',
  },
  {
    id: 'simulations',
    feature: 'simulation.view',
    title: 'Simulations',
    description:
      'Simulate different levels of budget increase or decrease and see the projected outcome before you change it.',
  },
  {
    // The id stays `rebalancing` — internal, pinned by `planAdditions.test.ts`, never rendered.
    id: 'rebalancing',
    feature: 'optimization.view',
    title: 'Cross-channel optimization',
    description: 'AI-driven budget allocation, applied across every channel automatically.',
  },
  {
    id: 'validation',
    feature: 'validation.view',
    title: 'Validation',
    description: "Measure how accurate each optimization's prediction was against what actually happened.",
  },
];

/**
 * The additions the Nexoya Starter plan does not already include.
 *
 * Locked-ness is DERIVED from `FEATURES`, so moving a capability into the Starter plan drops
 * its row from this list on its own. A hand-authored `locked: true` would drift from the plan
 * it claims to describe, silently, and the dialog would keep selling a capability the team had
 * just been given — the failure mode this whole module exists to prevent.
 *
 * The filter runs per call rather than at module scope so the plan can move underneath it;
 * `planAdditions.test.ts` mutates `FEATURES` and calls again, which is the only assertion in
 * that file a hardcoded list cannot pass. Nothing here assumes a row count — the renderer
 * must not either.
 */
export function planAdditions(): PlanAddition[] {
  return ADDITIONS.filter((row) => !isFeatureInPlan(row.feature, PLANS.STARTER));
}
